import { useScaBreakpoints } from "@scayla/core";
import { defineStore } from "pinia";
import type { Ref } from "vue";
import { ref, watch } from "vue";

import { $t } from "@/i18n";
import {
  LAYOUT_ROW_FIELD_GROUP_EXPAND_TYPE,
  OLDER_TABLE_ENTRIES_EXPAND_TYPE,
  TEMPLATE_SECTION_EXPAND_TYPE,
  useModuleSettingsStore,
} from "@/store/moduleSettings.ts";
import { useUserSettingsStore } from "@/store/userSettings";
import { isSettingsPage as checkIsSettingsPage } from "@/utils/isSettingsPage";
import type { NavNode, NavRouteQueryLookup } from "@/utils/navUtils";
import { findNavNodeByRouteQuery } from "@/utils/navUtils";

const { isAtLeastXL } = useScaBreakpoints();

export const LAYOUT_STORE_VERSION = "1.0.5";
export type NavItemConfig = Record<string, { expanded?: boolean | "auto"; favorite?: boolean }>;

export type LayoutBarId = "navigation" | "detailsBar" | "sideBar" | "viewBar" | "previewBar";

export type HeaderViewMode = "text" | "symbol" | "symbolAndText";

const isHeaderViewMode = (value: string): value is HeaderViewMode =>
  value === "text" || value === "symbol" || value === "symbolAndText";

export interface LayoutBarInfo {
  label?: string;
  icon?: string;
  visible: boolean;
  showTogglerInSubHeader?: boolean;
  showPreviewInSubHeader?: boolean;
  size?: number;
  minSize?: number;
  maxSize?: number;
}

const defaultBarVisibility: Readonly<Record<LayoutBarId, LayoutBarInfo["visible"]>> = {
  navigation: true,
  detailsBar: false,
  sideBar: true,
  viewBar: false,
  previewBar: false,
};

export const useLayoutStore = defineStore(
  "layoutStore",
  () => {
    const storeVersion = ref("");
    const navItemConfigs = ref<NavItemConfig>({});
    const userSettings = useUserSettingsStore();

    const sidebarNavContent = ref<"navigation" | "favorites">(
      userSettings.navbarView === "navigation" ? "navigation" : "favorites",
    );
    const sidebarNavMode = ref<"default" | "mini">(userSettings.navbarType === "default" ? "default" : "mini");
    const headerViewMode = ref<HeaderViewMode>(
      isHeaderViewMode(userSettings.headerViewMode) ? userSettings.headerViewMode : "text",
    );
    const breadcrumbsVisibility = ref(true);
    const bars = ref<Record<LayoutBarId, LayoutBarInfo>>(getDefaultBars());
    const previewFile = ref({
      url: "",
      type: "",
      name: "",
    });
    const moduleViewModes = ref<Record<string, "list" | "documents">>({});

    function setModuleViewMode(key: string, mode: "list" | "documents") {
      moduleViewModes.value[key] = mode;
    }

    function getModuleViewMode(key: string): "list" | "documents" | undefined {
      return moduleViewModes.value[key];
    }

    const recordFilesTabIndexes = ref<Record<string, number>>({});

    function setRecordFilesTabIndex(recordId: string, index: number) {
      recordFilesTabIndexes.value[recordId] = index;
    }

    function getRecordFilesTabIndex(recordId: string): number | undefined {
      return recordFilesTabIndexes.value[recordId];
    }

    const navigationNodes = ref<NavNode[]>([]);

    watch(
      [
        () => userSettings.navbarType,
        () => userSettings.navbarView,
        () => userSettings.headerViewMode,
        () => userSettings.isLoading,
      ],
      ([navbarType, navbarView, headerViewModeSetting, isLoading]) => {
        if (isLoading) return;
        if (!navbarType || !navbarView) return;

        const newMode = navbarType as "default" | "mini";
        const newView = navbarView as "navigation" | "favorites";

        if (sidebarNavMode.value !== newMode) {
          sidebarNavMode.value = newMode;
        }
        if (sidebarNavContent.value !== newView) {
          sidebarNavContent.value = newView;
        }
        if (
          headerViewModeSetting &&
          isHeaderViewMode(headerViewModeSetting) &&
          headerViewMode.value !== headerViewModeSetting
        ) {
          headerViewMode.value = headerViewModeSetting;
        }
      },
      { immediate: true },
    );

    watch(
      [() => userSettings.favorites, () => userSettings.isLoading],
      ([favorites, isLoading]) => {
        if (isLoading) return;
        if (!favorites) return;

        Object.entries(favorites).forEach(([id, isFavorite]) => {
          if (isFavorite) {
            if (navItemConfigs.value[id]?.favorite !== true) {
              navItemConfigs.value[id] = {
                ...navItemConfigs.value[id],
                favorite: true,
              };
            }
          }
        });

        Object.keys(navItemConfigs.value).forEach((id) => {
          if (favorites[id] === undefined && navItemConfigs.value[id]?.favorite === true) {
            navItemConfigs.value[id] = {
              ...navItemConfigs.value[id],
              favorite: false,
            };
          }
        });
      },
      { immediate: true },
    );

    function getDefaultBars() {
      return {
        navigation: {
          visible: true,
          size: 248,
          minSize: 248,
          maxSize: 448,
        },
        viewBar: {
          label: $t("common.label.viewbar"),
          visible: true,
          size: 288,
        },
        detailsBar: {
          visible: false,
          minSize: 324,
        },
        sideBar: {
          label: $t("common.label.sidebar"),
          visible: isAtLeastXL.value,
          showTogglerInSubHeader: true,
          size: 300,
          maxSize: 360,
          minSize: 300,
        },
        previewBar: {
          label: $t("common.label.previewbar"),
          visible: false,
          showPreviewInSubHeader: true,
          size: 320,
          minSize: 320,
          maxSize: 1200,
        },
      };
    }

    const hideableFieldsVisibility = ref(false);
    const moduleSettings = useModuleSettingsStore();

    function isSettingsPage(moduleId?: string | null): boolean {
      return checkIsSettingsPage(moduleId);
    }

    function set(id: string, newValue: boolean | "auto") {
      navItemConfigs.value[id] = {
        ...navItemConfigs.value[id],
        expanded: newValue,
      };
    }

    // All module-scoped expand caches (section, group, older-table-entries) read from the SAME
    // per-module settings payload, so rather than each cache fetching+filtering independently
    // (3 identical "all settings for this module" requests), one shared fetch below distributes
    // each returned setting to whichever registered cache owns its settingName.
    interface ExpandCacheRegistration<TValue extends boolean | "auto"> {
      settingName: string;
      cache: Ref<Record<string, Record<string, TValue>>>;
      parseValue: (raw: string) => TValue;
      onCacheUpdated?: (moduleId: string, key: string, value: TValue) => void;
    }

    const moduleExpandSettingsLoadedModules = ref<Set<string>>(new Set());
    const moduleExpandSettingsLoadingModules = ref<Set<string>>(new Set());
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const expandCacheRegistrations: ExpandCacheRegistration<any>[] = [];

    async function loadModuleExpandSettings(
      moduleId: string,
      forceRefresh = false,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onlyRegistration?: ExpandCacheRegistration<any>,
    ): Promise<void> {
      if (isSettingsPage(moduleId)) {
        moduleExpandSettingsLoadedModules.value.add(moduleId);
        return;
      }

      if (!moduleId) return;

      if (forceRefresh) {
        moduleExpandSettingsLoadedModules.value.delete(moduleId);
        // Only clear the cache slice belonging to the registration that asked for the refresh — the
        // other registered caches (e.g. group/older-table-entries when section is refreshed) keep
        // their already-loaded values undisturbed instead of flickering back to their defaults while
        // the shared re-fetch below is in flight.
        const registrationsToClear = onlyRegistration ? [onlyRegistration] : expandCacheRegistrations;
        registrationsToClear.forEach((registration) => delete registration.cache.value[moduleId]);
      }

      if (
        moduleExpandSettingsLoadedModules.value.has(moduleId) ||
        moduleExpandSettingsLoadingModules.value.has(moduleId)
      ) {
        return;
      }

      moduleExpandSettingsLoadingModules.value.add(moduleId);

      try {
        const settings = await moduleSettings.fetchModuleSettings(moduleId, true);
        if (settings?.items) {
          settings.items.forEach((setting) => {
            if (!setting.keySuffix || !setting.value) return;
            const registration = expandCacheRegistrations.find((r) => r.settingName === setting.name);
            if (!registration) return;

            const key = setting.keySuffix;
            const value = registration.parseValue(setting.value as string);

            if (!registration.cache.value[moduleId]) {
              registration.cache.value[moduleId] = {};
            }
            registration.cache.value[moduleId][key] = value;
            registration.onCacheUpdated?.(moduleId, key, value);
          });
          moduleExpandSettingsLoadedModules.value.add(moduleId);
        }
      } catch (error) {
        console.warn(`Failed to load expand settings for module ${moduleId}:`, error);
      } finally {
        moduleExpandSettingsLoadingModules.value.delete(moduleId);
      }
    }

    // Per-setting get/set helpers on top of the shared fetch above. The only real differences
    // between section, group, and older-table-entries are the setting name and whether the value
    // supports "auto" (section & group) or is plain boolean (older-table-entries).
    function createModuleExpandCache<TValue extends boolean | "auto">(config: {
      settingName: string;
      logLabel: string;
      parseValue: (raw: string) => TValue;
      serializeValue: (value: TValue) => string;
      onCacheUpdated?: (moduleId: string, key: string, value: TValue) => void;
    }) {
      const cache = ref<Record<string, Record<string, TValue>>>({});
      const registration: ExpandCacheRegistration<TValue> = {
        settingName: config.settingName,
        cache,
        parseValue: config.parseValue,
        onCacheUpdated: config.onCacheUpdated,
      };
      expandCacheRegistrations.push(registration);

      // Scoped to this registration: a forced reload only clears THIS cache's slice, leaving the
      // other registered caches (which ride along on the same shared fetch) untouched.
      async function loadModuleSettings(moduleId: string, forceRefresh = false): Promise<void> {
        await loadModuleExpandSettings(moduleId, forceRefresh, registration);
      }

      async function refreshModuleSettings(moduleId: string): Promise<void> {
        await loadModuleSettings(moduleId, true);
      }

      function getExpandState(moduleId: string, key: string, defaultExpanded?: TValue): TValue {
        const fallback = defaultExpanded ?? (true as TValue);
        // Trigger unconditionally (guarded internally by the shared loaded/loading sets) so a cache
        // entry restored from persisted storage still gets revalidated against the backend once per
        // session, instead of permanently skipping the fetch just because a (possibly stale) value
        // already exists.
        if (moduleId && !isSettingsPage(moduleId) && !moduleExpandSettingsLoadedModules.value.has(moduleId)) {
          void loadModuleExpandSettings(moduleId);
        }
        if (cache.value[moduleId]?.[key] !== undefined) {
          return cache.value[moduleId][key];
        }
        return fallback;
      }

      async function setExpandState(moduleId: string, key: string, value: TValue): Promise<void> {
        if (!cache.value[moduleId]) {
          cache.value[moduleId] = {};
        }
        cache.value[moduleId][key] = value;
        config.onCacheUpdated?.(moduleId, key, value);

        if (!isSettingsPage(moduleId)) {
          const stringValue = config.serializeValue(value);
          try {
            await moduleSettings.saveModuleSetting(moduleId, config.settingName, stringValue, key);
          } catch (error) {
            console.warn(`Failed to save ${config.logLabel} expand state for ${moduleId}/${key}:`, error);
            throw error;
          }
        }
      }

      return {
        cache,
        loadedModules: moduleExpandSettingsLoadedModules,
        loadModuleSettings,
        refreshModuleSettings,
        getExpandState,
        setExpandState,
      };
    }

    function parseAutoExpandValue(raw: string): boolean | "auto" {
      return raw === "auto" ? "auto" : !(raw === "never" || raw === "false");
    }

    function serializeAutoExpandValue(value: boolean | "auto"): string {
      return value === "auto" ? "auto" : !value ? "never" : "always";
    }

    function parseBooleanExpandValue(raw: string): boolean {
      return !(raw === "never" || raw === "false");
    }

    function serializeBooleanExpandValue(value: boolean): string {
      return value ? "always" : "never";
    }

    const groupCache = createModuleExpandCache<boolean | "auto">({
      settingName: LAYOUT_ROW_FIELD_GROUP_EXPAND_TYPE,
      logLabel: "group",
      parseValue: parseAutoExpandValue,
      serializeValue: serializeAutoExpandValue,
    });
    const groupExpandCache = groupCache.cache;
    const groupModules = groupCache.loadedModules;
    const loadModuleGroupSettings = groupCache.loadModuleSettings;
    const refreshModuleGroupSettings = groupCache.refreshModuleSettings;
    const getGroupExpandState = groupCache.getExpandState;
    const setGroupExpandState = groupCache.setExpandState;

    const olderTableEntriesCache = createModuleExpandCache<boolean>({
      settingName: OLDER_TABLE_ENTRIES_EXPAND_TYPE,
      logLabel: "older-table-entries",
      parseValue: parseBooleanExpandValue,
      serializeValue: serializeBooleanExpandValue,
    });
    const olderTableEntriesExpandCache = olderTableEntriesCache.cache;
    const olderTableEntriesModules = olderTableEntriesCache.loadedModules;
    const loadModuleOlderTableEntriesSettings = olderTableEntriesCache.loadModuleSettings;
    const getOlderTableEntriesExpandState = olderTableEntriesCache.getExpandState;
    const setOlderTableEntriesExpandState = olderTableEntriesCache.setExpandState;

    const sectionCache = createModuleExpandCache<boolean | "auto">({
      settingName: TEMPLATE_SECTION_EXPAND_TYPE,
      logLabel: "section",
      parseValue: parseAutoExpandValue,
      serializeValue: serializeAutoExpandValue,
      onCacheUpdated: (_moduleId, sectionId, value) => set(sectionId, value),
    });
    const loadModuleSectionSettings = sectionCache.loadModuleSettings;
    const refreshModuleSectionSettings = sectionCache.refreshModuleSettings;
    const setSectionExpandState = sectionCache.setExpandState;

    // Section keeps one extra fallback the shared cache doesn't know about: an already-resolved
    // navItemConfigs entry (synced via onCacheUpdated above) takes precedence over triggering a
    // fresh load, so this stays a bespoke wrapper instead of using sectionCache.getExpandState directly.
    function getSectionExpandState(
      moduleId: string,
      sectionId: string,
      defaultExpanded: boolean | "auto" = true,
    ): boolean | "auto" {
      if (sectionCache.cache.value[moduleId]?.[sectionId] !== undefined) {
        return sectionCache.cache.value[moduleId][sectionId];
      }
      const navConfig = navItemConfigs.value[sectionId];
      if (navConfig?.expanded !== undefined) {
        return navConfig.expanded;
      }
      if (moduleId && !isSettingsPage(moduleId) && !sectionCache.loadedModules.value.has(moduleId)) {
        void sectionCache.loadModuleSettings(moduleId);
      }
      return defaultExpanded;
    }

    function get(id: string, defaultExpanded = false) {
      if (!navItemConfigs.value[id]) {
        navItemConfigs.value[id] = { expanded: defaultExpanded ? true : undefined };
      }
      return navItemConfigs.value[id];
    }

    function batchSetExpanded(ids: string[], value: boolean) {
      const updates: NavItemConfig = {};
      for (const id of ids) {
        updates[id] = {
          ...navItemConfigs.value[id],
          expanded: value,
        };
      }
      navItemConfigs.value = { ...navItemConfigs.value, ...updates };
    }

    async function setFavorite(id: string, newValue: boolean) {
      navItemConfigs.value[id] = {
        ...navItemConfigs.value[id],
        favorite: newValue,
      };

      const currentFavorites: Record<string, boolean> = {};
      Object.entries(navItemConfigs.value).forEach(([itemId, config]) => {
        if (config.favorite === true) {
          currentFavorites[itemId] = true;
        }
      });

      if (newValue) {
        currentFavorites[id] = true;
      } else {
        delete currentFavorites[id];
      }

      try {
        await userSettings.setFavorites(currentFavorites);
      } catch (error) {
        console.error("Failed to persist favorites:", error);
        navItemConfigs.value[id] = {
          ...navItemConfigs.value[id],
          favorite: !newValue,
        };
      }
    }

    function isFavorite(id: string): boolean {
      return !!navItemConfigs.value[id]?.favorite;
    }

    async function setSidebarNavContent(content: "navigation" | "favorites") {
      if (sidebarNavContent.value === content) return;

      sidebarNavContent.value = content;
      try {
        await userSettings.setNavbarView(content);
      } catch (error) {
        console.error("Failed to persist navbar view:", error);
      }
    }

    async function setSidebarNavMode(mode: "default" | "mini") {
      if (sidebarNavMode.value === mode) return;

      sidebarNavMode.value = mode;
      try {
        await userSettings.setNavbarType(mode);
      } catch (error) {
        console.error("Failed to persist navbar type:", error);
      }
    }

    async function setHeaderViewMode(mode: HeaderViewMode) {
      if (headerViewMode.value === mode) return;

      headerViewMode.value = mode;
      try {
        await userSettings.setHeaderViewMode(mode);
      } catch (error) {
        console.error("Failed to persist header view mode:", error);
      }
    }

    function toggleBarVisibility(id: LayoutBarId) {
      bars.value[id].visible = !bars.value[id].visible;
    }

    function setBarVisibility(id: LayoutBarId | LayoutBarId[], visible: boolean) {
      const ids = typeof id === "string" ? [id] : id;
      ids.forEach((barId) => {
        bars.value[barId].visible = visible;
      });
    }

    function toggleBreadcrumbVisibility() {
      breadcrumbsVisibility.value = !breadcrumbsVisibility.value;
    }

    function updateBarSize(id: LayoutBarId, size: number) {
      if (bars.value[id]) {
        bars.value[id].size = size;
      }
    }

    function setStoreVersion(version: string) {
      storeVersion.value = version;
    }

    function resetBars() {
      bars.value = getDefaultBars();
    }

    function toggleHideableFieldsVisibility() {
      hideableFieldsVisibility.value = !hideableFieldsVisibility.value;
    }

    function setHideableFieldsVisibility(value: boolean) {
      hideableFieldsVisibility.value = value;
    }

    function setPreviewFile(url: string, type: string, name: string) {
      previewFile.value.url = url;
      previewFile.value.type = type;
      previewFile.value.name = name;
    }

    function clearPreviewFile() {
      previewFile.value.url = "";
      previewFile.value.type = "";
      previewFile.value.name = "";
    }

    function setNavigationNodes(nodes: NavNode[]) {
      navigationNodes.value = nodes;
    }

    function getNavigationLabel(query: NavRouteQueryLookup) {
      return findNavNodeByRouteQuery(navigationNodes.value, query)?.label;
    }

    return {
      storeVersion,
      navItemConfigs: navItemConfigs,
      bars,
      previewFile,
      navigationNodes,
      set,
      get,
      batchSetExpanded,
      loadModuleSectionSettings,
      refreshModuleSectionSettings,
      getSectionExpandState,
      setSectionExpandState,
      setBarVisibility,
      toggleBarVisibility,
      defaultBarVisibility,
      breadcrumbsVisibility,
      toggleBreadcrumbVisibility,
      updateBarSize,
      setStoreVersion,
      resetBars,
      hideableFieldsVisibility,
      toggleHideableFieldsVisibility,
      setHideableFieldsVisibility,
      setPreviewFile,
      clearPreviewFile,
      setNavigationNodes,
      getNavigationLabel,
      setFavorite,
      isFavorite,
      sidebarNavContent,
      setSidebarNavContent,
      sidebarNavMode,
      setSidebarNavMode,
      headerViewMode,
      setHeaderViewMode,
      moduleViewModes,
      setModuleViewMode,
      getModuleViewMode,
      recordFilesTabIndexes,
      setRecordFilesTabIndex,
      getRecordFilesTabIndex,
      groupExpandCache,
      groupModules,
      loadModuleGroupSettings,
      refreshModuleGroupSettings,
      getGroupExpandState,
      setGroupExpandState,
      olderTableEntriesExpandCache,
      olderTableEntriesModules,
      loadModuleOlderTableEntriesSettings,
      getOlderTableEntriesExpandState,
      setOlderTableEntriesExpandState,
    };
  },
  {
    persist: {
      pick: [
        "navItemConfigs",
        "bars",
        "breadcrumbsVisibility",
        "hideableFieldsVisibility",
        "sidebarNavContent",
        "sidebarNavMode",
        "headerViewMode",
        "storeVersion",
        "olderTableEntriesExpandCache",
      ],
    },
  },
);
